import React, { ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Settings,
  Package,
  Gift,
  GraduationCap,
  Shield,
  BarChart3,
  Power,
  ShoppingCart,
} from "lucide-react";

import { isUserAdmin, isUserStudent } from "@/services/shared/auth/auth.service";
import { usePrivate } from "@/contexts/PrivateContext";
import { SubscriptionPastDueBanner } from "@/components/student/SubscriptionPastDueBanner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import logo_icon from "@/assets/images/logos/ofcourse-bar-logo.svg";
import { UserAvatar } from "@/components/user/UserAvatar";

interface NavIconProps {
  icon: React.ReactNode;
  label: string;
  className?: string;
  active?: boolean;
  link?: string;
  onClick?: () => void;
}

const NavIcon: React.FC<NavIconProps> = ({
  icon,
  label,
  className = "",
  active = false,
  link = "#",
  onClick,
}) => {
  const content = (
    <div
      className={`
        w-10 h-10 !p-0
        flex items-center justify-center 
        ${active ? "bg-black text-white" : "bg-gray-100 text-gray-400"}
        rounded-full 
        ${className}
        ${onClick ? "cursor-pointer" : ""}
      `}
      aria-label={label}
      onClick={onClick}
    >
      {icon}
    </div>
  );

  if (onClick) {
    return content;
  }

  return (
    <Link
      className={`
        w-10 h-10 !p-0
        flex items-center justify-center 
        ${active ? "bg-black text-white" : "bg-gray-100 text-gray-400"}
        rounded-full 
        ${className}
      `}
      aria-label={label}
      to={link}
    >
      {icon}
    </Link>
  );
};

const adminNavItems = [
  {
    icon: <Home className="w-5 h-5" />,
    label: "Dashboard",
    link: "/admin/dashboard",
  },
  {
    icon: <Package className="w-5 h-5" />,
    label: "Pacotes",
    link: "/admin/packages",
  },
  {
    icon: <GraduationCap className="w-5 h-5" />,
    label: "Educational",
    link: "/admin/educational",
  },
  {
    icon: <Shield className="w-5 h-5" />,
    label: "Access",
    link: "/admin/access",
  },
  {
    icon: <ShoppingCart className="w-5 h-5" />,
    label: "Pedidos",
    link: "/admin/orders",
  },
  {
    icon: <BarChart3 className="w-5 h-5" />,
    label: "Relatórios",
    link: "/admin/reports",
  },
  {
    icon: <Gift className="w-5 h-5" />,
    label: "Vouchers",
    link: "/admin/vouchers",
  },
];

const AdminNavIcons = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  return (
    <>
      {adminNavItems.map((item, index) => (
        <NavIcon
          key={index}
          icon={item.icon}
          label={item.label}
          link={item.link}
          active={
            item.link === "/admin/orders"
              ? currentPath.startsWith("/admin/orders")
              : currentPath === item.link
          }
        />
      ))}
    </>
  );
};

const studentNavItems = [
  {
    icon: <Home className="w-5 h-5" />,
    label: "Dashboard",
    link: "/student/dashboard",
  },
  {
    icon: <Package className="w-5 h-5" />,
    label: "Pacotes",
    link: "/student/packages",
  },
];

const StudentNavIcons = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  console.log("StudentNavIcons - Rendering with items:", studentNavItems);
  console.log("Current path:", currentPath);
  console.log("isUserStudent() returns:", isUserStudent());
  return (
    <>
      {studentNavItems.map((item, index) => {
        console.log(`Rendering item ${index}:`, item);
        return (
          <NavIcon
            key={index}
            icon={item.icon}
            label={item.label}
            link={item.link}
            active={currentPath === item.link || currentPath.startsWith(item.link + "/")}
          />
        );
      })}
    </>
  );
};

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const { logout, name } = usePrivate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
    logout();
    setLogoutDialogOpen(false);
    navigate("/login");
  };

  return (
    <div className="min-h-screen min-w-full">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-20 bg-gray-50 flex flex-col items-center py-8 z-50">
        {/* User Avatar — link to profile settings */}
        <Link
          to="/student/settings"
          className="mb-10 flex-shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50"
          aria-label={
            name
              ? `Abrir configurações do perfil de ${name}`
              : "Abrir configurações do perfil"
          }
          title={name ? `Perfil de ${name}` : "Seu perfil"}
        >
          <UserAvatar size="md" decorative />
        </Link>

        {/* Navigation Icons */}
        <nav className="flex flex-col items-center space-y-7">
          {(() => {
            console.log("DashboardLayout - Rendering navigation");
            console.log("isUserAdmin():", isUserAdmin());
            console.log("isUserStudent():", isUserStudent());
            
            // Verificar o token JWT
            const token = localStorage.getItem('token');
            console.log("JWT token exists:", !!token);
            if (token) {
              try {
                const tokenParts = token.split('.');
                if (tokenParts.length >= 2) {
                  const base64Url = tokenParts[1];
                  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                  const jsonPayload = atob(base64);
                  console.log("JWT payload:", jsonPayload);
                } else {
                  console.log("Token does not have expected parts");
                }
              } catch (error) {
                console.log("Error decoding token:", error);
              }
            }
            
            return null;
          })()}
          {isUserAdmin() && <AdminNavIcons />}
          {isUserStudent() && <StudentNavIcons />}
        </nav>

        {/* Bottom Icons */}
        <div className="mt-auto flex flex-col items-center space-y-7 mb-10">
          <NavIcon
            icon={<Settings className="w-5 h-5" />}
            label="Minhas Configurações"
            link="/student/settings"
            active={currentPath === "/student/settings"}
          />
          <NavIcon
            icon={
              <div className="w-full h-full rounded-full">
                <img src={logo_icon} alt="Logo" />
              </div>
            }
            label="Custom"
          />
          <NavIcon
            icon={<Power className="w-5 h-5" />}
            label="Sair"
            className="bg-gray-100 text-gray-400 hover:bg-gray-200"
            onClick={handleLogout}
          />
        </div>
      </aside>
      <main className="min-h-screen w-full pl-20">
        {isUserStudent() ? <SubscriptionPastDueBanner /> : null}
        {children}
      </main>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Saída</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja sair do sistema?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmLogout}>
              Sair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
